<template>
  <div class="container-fluid py-4">
    <div class="d-flex justify-content-between align-items-center mb-4 header-clinique flex-wrap gap-2">
      <div class="d-flex align-items-center gap-3">
        <i class="bi bi-hospital fs-2 text-primary"></i>
        <div>
          <h2 class="fw-bold mb-0">Hospitalisations</h2>
          <small class="text-muted">{{ total.toLocaleString('fr-FR') }} hospitalisation(s)</small>
        </div>
      </div>
    </div>

    <div class="card p-3 mb-3 shadow-sm rounded card-clinique">
      <div class="row g-2 align-items-end">
        <div class="col-12 col-md-4">
          <label class="form-label fw-semibold small mb-1">Rechercher</label>
          <input v-model="recherche" type="search" class="form-control form-control-clinique" placeholder="N° d'hospitalisation, patient, téléphone, chambre, lit…">
        </div>
        <div class="col-6 col-md-2">
          <label class="form-label fw-semibold small mb-1">Statut</label>
          <select v-model="statut" class="form-select form-select-clinique">
            <option value="">Tous</option>
            <option v-for="s in statuts" :key="s" :value="s">{{ s }}</option>
          </select>
        </div>
        <div class="col-6 col-md-2">
          <label class="form-label fw-semibold small mb-1">Entrée du</label>
          <input v-model="du" type="date" class="form-control form-control-clinique">
        </div>
        <div class="col-6 col-md-2">
          <label class="form-label fw-semibold small mb-1">au</label>
          <input v-model="au" type="date" class="form-control form-control-clinique">
        </div>
        <div class="col-6 col-md-2">
          <label class="form-label fw-semibold small mb-1">Trier par</label>
          <div class="d-flex gap-1">
            <select v-model="tri" class="form-select form-select-clinique">
              <option value="hos.dateEntree">Date d'entrée</option>
              <option value="hos.dateSortiePrevue">Sortie prévue</option>
              <option value="p.nom">Patient</option>
              <option value="hos.montantFacture">Montant</option>
              <option value="hos.statut">Statut</option>
            </select>
            <select v-model="sens" class="form-select form-select-clinique w-auto" aria-label="Ordre">
              <option value="desc">↓</option>
              <option value="asc">↑</option>
            </select>
          </div>
        </div>
      </div>
    </div>

    <div v-if="erreur" class="alert alert-danger d-flex justify-content-between align-items-center">
      <span>{{ erreur }}</span>
      <button class="btn btn-sm btn-outline-danger" @click="charger">Réessayer</button>
    </div>

    <div class="card shadow-sm rounded card-clinique">
      <div class="table-responsive" :class="{ 'opacity-50': chargement }">
        <table class="table align-middle text-center mb-0 table-hover modern-table-clinique w-100">
          <thead>
            <tr>
              <th>#</th>
              <th>Entrée</th>
              <th>Patient</th>
              <th class="d-none d-sm-table-cell">Chambre</th>
              <th class="d-none d-md-table-cell">Type</th>
              <th class="d-none d-sm-table-cell">Lit</th>
              <th>Sortie prévue</th>
              <th class="d-none d-md-table-cell">Statut</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="(hos, i) in lignes" :key="hos.id" :class="{ 'ligne-aujourdhui': isSortieAujourdhui(hos) }">
              <td class="text-muted">{{ rang(i) }}</td>
              <td>{{ formatDate(hos.dateEntree) }}</td>
              <td class="text-start">
                <span class="fw-semibold">{{ hos.patient_prenom }} {{ hos.patient_nom }}</span>
                <div v-if="hos.numHospitalisation" class="small text-muted">{{ hos.numHospitalisation }}</div>
              </td>
              <td class="d-none d-sm-table-cell">{{ hos.chambre_numero || '—' }}</td>
              <td class="d-none d-md-table-cell">{{ hos.chambre_type_nom || '—' }}</td>
              <td class="d-none d-sm-table-cell">{{ hos.lit_numero || '—' }}</td>
              <td :class="getColorClasse(hos)">{{ formatDate(hos.dateSortiePrevue) }}</td>
              <td class="d-none d-md-table-cell fw-semibold">{{ hos.statut || '—' }}</td>
              <td>
                <div class="dropdown">
                  <button class="btn btn-sm btn-outline-secondary dropdown-toggle" type="button" data-bs-toggle="dropdown" v-dropdown-fixed>Actions</button>
                  <ul class="dropdown-menu">
                    <li><a class="dropdown-item" href="#" @click.prevent="details(hos)">Détails</a></li>
                    <li><a class="dropdown-item" href="#" @click.prevent="imprimerHospitalisation(hos)"><i class="bi bi-printer me-1"></i>Imprimer</a></li>
                    <li v-if="!hos.dateSortieReelle && !String(hos.statut || '').toLowerCase().startsWith('annul') && $store.getters.hasPermission('HOSP_ANNULER')">
                      <a class="dropdown-item text-danger" href="#" @click.prevent="annulerHospitalisation(hos)"><i class="bi bi-x-circle me-1"></i>Annuler</a>
                    </li>
                  </ul>
                </div>
              </td>
            </tr>
            <tr v-if="!chargement && !lignes.length">
              <td colspan="9" class="text-center text-muted py-4">{{ recherche || statut || du || au ? 'Aucune hospitalisation ne correspond à la recherche.' : 'Aucune hospitalisation enregistrée.' }}</td>
            </tr>
          </tbody>
        </table>
      </div>
      <div class="px-3 pb-3">
        <Pagination v-model:page="page" v-model:par-page="parPage" :total="total" />
      </div>
    </div>
  </div>
</template>

<script>
import api, { messageErreur, notifier } from '@/api';
import dayjs from 'dayjs';
import Pagination from '@/components/commun/Pagination.vue';
import { listeServeur } from '@/components/commun/listeServeur';
import { demanderMotif } from '@/components/commun/dialogue';
import { imprimer, e, fcfa, blocInfos, nomComplet } from '@/impression/impression';

export default {
  name: 'ListeHospitalisation',
  components: { Pagination },
  mixins: [listeServeur('hospitalisations', { tri: 'hos.dateEntree', sens: 'desc', parPage: 25 })],
  data() {
    return {
      du: '',
      au: '',
      statut: '',
      statuts: ['En cours', 'Clôturée', 'Annulée'],
    };
  },
  watch: {
    du() { this.rechargerListe(); },
    au() { this.rechargerListe(); },
    statut() { this.rechargerListe(); },
  },
  methods: {
    filtresListe() {
      return { du: this.du, au: this.au, statut: this.statut };
    },
    formatDate(date) {
      return date ? dayjs(date).format('DD/MM/YYYY') : '—';
    },
    creation() {
      this.$router.push({ name: 'create_hospitalisation' });
    },
    modifier(id) {
      this.$router.push({ name: 'edit_hospitalisation', params: { id } });
    },
    details(hos) {
      return this.$router.push({ name: 'details_hospitalisation', params: { id: hos.id } });
    },
    isSortieAujourdhui(hos) {
      return !!hos.dateSortiePrevue && dayjs(hos.dateSortiePrevue).isSame(dayjs(), 'day');
    },
    getColorClasse(hosp) {
      const aujourdHui = dayjs().startOf('day');
      const sortie = hosp.dateSortiePrevue ? dayjs(hosp.dateSortiePrevue).startOf('day') : null;
      if (hosp.statut === 'En cours' && sortie) {
        return sortie.isBefore(aujourdHui) ? 'text-danger fw-bold' : 'text-success fw-bold';
      }
      if (hosp.statut === 'Clôturée') {
        return hosp.estPaye === 'Payée' ? 'text-primary fw-bold' : 'text-warning fw-bold';
      }
      return '';
    },
    async annulerHospitalisation(hosp) {
      const r = await demanderMotif({
        titre: `Annuler l'hospitalisation ${hosp.numHospitalisation || ''}`,
        message: "Elle restera dans l'historique avec le motif et le lit sera libéré. Ses encaissements doivent d'abord être annulés ou remboursés.",
        libelleValider: "Annuler l'hospitalisation",
      });
      if (!r) return;
      try {
        await api.post(`hospitalisations/${hosp.id}/annuler`, { motif: r.motif });
        notifier('Hospitalisation annulée, lit libéré.', 'success');
        this.charger();
      } catch (error) {
        notifier(messageErreur(error, "L'annulation a échoué."), 'danger');
      }
    },
    async imprimerHospitalisation(hos) {
      const reference = hos.numHospitalisation || `HOS-${hos.id}`;
      const corps = `
        ${blocInfos([
          ['Patient', nomComplet({ nom: hos.patient_nom, prenom: hos.patient_prenom }) || '—'],
          ['Chambre', hos.chambre_numero || '—'],
          ['Lit', hos.lit_numero || '—'],
          ['Montant', fcfa(hos.montantFacture)],
          ['Statut', hos.statut || '—'],
        ])}
        <table class="lignes">
          <tbody>
            <tr><td>Date d'entrée</td><td>${e(this.formatDate(hos.dateEntree))}</td></tr>
            <tr><td>Date de sortie prévue</td><td>${e(this.formatDate(hos.dateSortiePrevue))}</td></tr>
            <tr><td>Date de sortie réelle</td><td>${e(this.formatDate(hos.dateSortieReelle))}</td></tr>
          </tbody>
        </table>`;
      await imprimer({
        titre: "Reçu d'hospitalisation",
        numero: reference,
        date: this.formatDate(hos.dateEntree),
        corps,
        signature: true,
        format: 'A5',
      });
    },
  },
};
</script>

<style scoped>
.header-clinique { padding: 15px 20px; background-color: #e9f5ff; border-radius: 12px; }
.card-clinique { background: #f8fafd; border-radius: 12px; border: none; }
.form-control-clinique, .form-select-clinique { border-radius: 8px; box-shadow: none; }
.modern-table-clinique th { background: #eef5ff; font-weight: 600; }
.ligne-aujourdhui td { background-color: #fff8e1; }
@media (max-width: 767px) {
  .table td, .table th { padding: 0.4rem 0.5rem; font-size: 0.8rem; }
}
</style>
